/* Copyright (c) 2020-present CloudIO Inc */

import {
  Path,
  useStateXAtom,
  useStateXAtomValue,
  useStateXGetter,
} from '@cloudio-saas/statex';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Popover,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import zIndex from 'constants/ZIndex';
import {
  hideTipsAtom,
  pulseHiddenStateAtom,
  useDeveloperMode,
} from 'designer/atoms';
import Markdown from 'layout/Sidebar/panels/ComponentsView/Markdown';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useState } from 'react';
import useUser from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    position: 'absolute',
    display: 'inline-block',
    '&:hover $root': {
      transform: 'scale(2)',
    },
    '&:hover $root$fixedSize': {
      transform: 'none',
    },
    cursor: 'default',
    zIndex: zIndex.Model,
  },
  fixedSize: {},
  root: {
    display: 'block',
    opacity: '0.8',
    outline: 'none',
    borderRadius: '100%',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.primary.main,
    position: 'relative',
  },
  after: {
    animation: '$pulse 3s linear infinite',
    borderRadius: '100%',
    content: '""',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  before: {
    animation: '$pulse 3s linear infinite',
    animationDelay: '1s',
    borderRadius: '100%',
    content: '""',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  inner: {
    borderRadius: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: -1,
  },
  innerBefore: {
    animation: '$pulse 3s linear infinite',
    animationDelay: '5s',
    borderRadius: '100%',
    content: '""',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  innerAfter: {
    animation: '$pulse 3s linear',
    animationDelay: '2s',
    borderRadius: '100%',
    content: '""',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  cover: {
    borderRadius: '100%',
    content: '""',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 1,
    backgroundColor: theme.palette.primary.main,
  },
  cursor: {
    cursor: 'pointer',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
      transform: 'scale(1, 1)',
    },
    '100%': {
      opacity: 0,
      transform: 'scale(5, 5)',
    },
  },
  primary: {
    backgroundColor: theme.palette.primary.main,
  },
  actions: {
    justifyContent: 'flex-end',
  },
  card: {
    borderTop: `4px solid ${theme.palette.primary.main}`,
    minWidth: 300,
    maxWidth: 400,
  },
}));

export interface PulseProps {
  appUid: string;
  pageId: string;
  itemId: string;
  message: string;
  top?: string;
  right?: string;
  left?: string;
  bottom?: string;
  hideDismiss?: boolean;
  markdown?: boolean;
  onClick?: () => void;
  hideMessageBox?: boolean;
}

function Pulse(
  {
    appUid,
    pageId,
    itemId,
    message,
    top,
    right,
    left,
    bottom,
    hideDismiss,
    markdown,
    onClick,
    hideMessageBox,
  }: PulseProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const { userName } = useUser();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const platform = usePlatform();
  const [hidden, setHidden] = useStateXAtom(pulseHiddenStateAtom, {
    params: { pageId, itemId, userName },
  });
  const hideTips = useStateXAtomValue(hideTipsAtom, {
    params: { userName },
  });
  const devMode = useDeveloperMode(appUid, pageId);
  const [devHidden, setDevHidden] = useState(false);
  const get = useStateXGetter();

  const classes = useStyles();
  const size = 8;
  const fixedSize = false;
  const hasClick = true;
  const className = clsx(classes.root, {
    [classes.fixedSize]: fixedSize,
    [classes.cursor]: hasClick,
  });

  if (
    (hidden && !devMode) ||
    devHidden ||
    (hideTips && pageId === '$sidebar$')
  ) {
    return null;
  }

  return (
    <>
      <div
        ref={ref}
        className={classes.wrapper}
        style={{
          top,
          right,
          left,
          bottom,
        }}
        role="none"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!hideMessageBox) {
            setAnchorEl(e.currentTarget);
          }
          onClick?.();
        }}
      >
        <span
          className={className}
          style={{
            height: size,
            width: size,
          }}
        >
          <span className={classes.before} />
          <span className={classes.after} />
          <span className={classes.innerBefore} />
          <span className={classes.innerAfter} />
          <span className={classes.inner} />
          <span className={classes.cover} />
        </span>
      </div>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Card className={classes.card}>
          <CardContent>
            {markdown ? (
              <Markdown>{message}</Markdown>
            ) : (
              <Typography>{message || 'Change me!'}</Typography>
            )}
          </CardContent>
          {!hideDismiss && (
            <CardActions disableSpacing className={classes.actions}>
              <Button
                data-testid={`dismiss-${itemId}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setAnchorEl(null);
                  if (!devMode) {
                    const pulse = get(
                      ['db', 'IoUserSettings', userName, 'md', 'pulse'],
                      undefined,
                    ) as Record<string, Record<string, boolean>>;
                    let value: unknown = true;
                    let path: Path;
                    if (typeof pulse === 'object' && pulse !== null) {
                      if (typeof pulse[pageId] === 'object') {
                        value = true;
                        path = ['pulse', pageId, itemId];
                      } else {
                        value = { [itemId]: true };
                        path = ['pulse', pageId];
                      }
                    } else {
                      value = { [pageId]: { [itemId]: true } };
                      path = ['pulse'];
                    }
                    setHidden(true);
                    await platform.postAtom(
                      {
                        ds: 'IoUserSettings',
                        key: userName,
                        path,
                        value,
                        pk: { userName },
                      },
                      { appUid },
                    );
                  } else {
                    setDevHidden(true);
                  }
                }}
              >
                Dismiss
              </Button>
            </CardActions>
          )}
        </Card>
      </Popover>
    </>
  );
}

const PulseWithRef = React.forwardRef(Pulse);

export function MenuItemPulse({
  pulseMessage,
  pulseKey,
  markdown,
}: {
  pulseMessage: string;
  pulseKey: string;
  markdown?: boolean;
}) {
  return (
    <PulseWithRef
      appUid="cloudio"
      itemId={pulseKey}
      message={pulseMessage}
      pageId="$sidebar$"
      right="24px"
      top="calc(50% - 4px)"
      hideDismiss={false}
      markdown={markdown}
    />
  );
}

export function IconPulse({
  pulseMessage,
  pulseKey,
  markdown,
}: {
  pulseMessage: string;
  pulseKey: string;
  markdown?: boolean;
}) {
  return (
    <PulseWithRef
      appUid="cloudio"
      itemId={pulseKey}
      message={pulseMessage}
      pageId="$sidebar$"
      right="calc(50% - 4px)"
      top="calc(50% - 4px)"
      hideDismiss={false}
      markdown={markdown}
    />
  );
}
export default PulseWithRef;
